/**
 * Valid currency code that excludes the UNDEFINED sentinel value and unhandledEnumCase symbol.
 *
 * NOTE: We intentionally exclude the unhandledEnumCase symbol to avoid type complexity
 * at integration boundaries with GMST and external APIs. This means we lose compile-time
 * detection of new currencies during the window between backend adding them and frontend
 * regenerating types, but this is acceptable since:
 * 1. Types are regenerated regularly as part of development
 * 2. The impact is minimal (currency shows as undefined temporarily)
 * 3. We avoid 100+ boundary conversions throughout the codebase
 *
 * TODO: Once all GMST CurrencyCode usage is migrated to this type, we could consider
 * adding the symbol back if the forward compatibility benefit outweighs the complexity cost.
 */

/**
 * Valid country code that excludes the UNDEFINED sentinel value and unhandledEnumCase symbol.
 * See CurrencyCode documentation for rationale.
 */

/**
 * Type guard that checks if a value is a non-null string suitable for use as a CurrencyCode.
 *
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 * Use this for trusted backend responses where you can assume any valid string is a valid currency.
 *
 * For untrusted input (like query parameters), use isValidCurrencyCodeForQueryParam instead.
 *
 * @example
 * // ✅ Good: checking backend response
 * if (isCurrencyCodeString(response.currencyCode)) {
 *   return response.currencyCode;
 * }
 *
 * // ❌ Bad: checking query parameter
 * if (isCurrencyCodeString(queryParam)) { // Don't do this!
 *   return queryParam;
 * }
 */
export function isCurrencyCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}

/**
 * Extracts the string value from a CurrencyCode, filtering out symbol types.
 * Returns undefined if the value is not a string.
 *
 * Use this when you need to pass a CurrencyCode to APIs or systems that only
 * accept string values (e.g., tracking, mutations, URL params).
 *
 * @example
 * const currencyStr = getCurrencyCodeString(currencyCode);
 * if (currencyStr) {
 *   trackEvent({ currency: currencyStr });
 * }
 *
 * @param code - The currency code to extract string from
 * @returns The string value if code is a string, undefined otherwise
 */
export function getCurrencyCodeString(code) {
  return typeof code === 'string' ? code : undefined;
}

/**
 * Type guard to check if a value is a valid CountryCode string.
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 * Returns true for any valid string value, trusting the backend to only return valid country codes.
 */
export function isCountryCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}

/**
 * Type guard to validate untrusted currency codes (like query params) against available currencies.
 * Use this for user input that needs validation against the actual list of available currencies.
 */
export function isValidCurrencyCodeForQueryParam(value, availableCurrencyCodes) {
  if (!isCurrencyCodeString(value)) {
    return false;
  }

  // At this point, value is a string (isCurrencyCodeString ensures this)
  // Validate against the actual list of available currencies
  const upperValue = value.toUpperCase();
  return availableCurrencyCodes.some(code => typeof code === 'string' && code.toUpperCase() === upperValue);
}

/**
 * Resolves which currency code should be used based on validation and constraints.
 *
 * Resolution priority:
 * 1. If currency is locked and requested code differs, returns the locked currency
 * 2. If requested code is valid and in available list, returns the requested currency
 * 3. Otherwise, falls back to the displayed currency
 *
 * This is useful when handling user-initiated currency changes (picker selection, query params)
 * where you need to respect locked currencies and validate against available options.
 *
 * @param availableCurrencyCodes - List of currencies the user can select from
 * @param lockedCurrencyCode - If set, the currency is locked and cannot be changed
 * @param displayedCurrencyCode - Current currency being displayed (used as fallback)
 * @param currencyCode - The requested currency code to validate
 * @returns The resolved currency code to use
 */
export function resolveCurrencyCode({
  availableCurrencyCodes,
  lockedCurrencyCode,
  displayedCurrencyCode,
  currencyCode
}) {
  const isDifferentFromLocked = lockedCurrencyCode !== currencyCode;
  if (lockedCurrencyCode && isDifferentFromLocked) {
    return lockedCurrencyCode;
  }
  if (isCurrencyCodeString(currencyCode) && availableCurrencyCodes.includes(currencyCode)) {
    return currencyCode;
  }
  return displayedCurrencyCode;
}

/**
 * Local definition of CurrencyStore that uses CHIRP-generated types.
 * This replaces the legacy type from growth-monetization-service-types.
 */